import { HillList } from '@/components/hill-list';
import { Button } from '@/components/ui/button';
import { hillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { sessionQueryOptions } from '@/lib/session-query';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

/**
 * `/archive` - completed hills, ordered newest-archived-first.
 *
 * Mirrors the home list (`/hills`) in shape - same `<HillList>` component
 * with `mode="archive"` so the cards open `/archive/$hillId` rather than
 * `/hills/$hillId`, and the empty-state copy matches the page intent.
 *
 * Auth guard runs in `beforeLoad` so an unauthenticated visitor is
 * redirected before any component mounts (no flicker of the list skeleton
 * before the /login redirect lands).
 *
 * The list query is fired from the component, not via a loader, so the
 * page shell paints immediately while the fetch resolves.
 */
export const Route = createFileRoute('/archive/')({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
    if (!session) {
      throw redirect({ to: '/login' });
    }
  },
  component: ArchivePage,
});

/** Exported for testing - mirrors the pattern in `routes/hills.index.tsx`. */
export function ArchivePage() {
  const archivedQuery = useQuery({
    queryKey: queryKeys.hillsList('done'),
    queryFn: () => hillsApi.list('done'),
    staleTime: 30_000,
  });

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-bold text-3xl text-brand-900 tracking-tight">Archive</h1>
        <p className="text-base text-slate-600">
          Hills marked as Done. Snapshots stay viewable; the chart is read-only.
        </p>
      </header>

      {archivedQuery.isLoading ? (
        <output
          aria-busy="true"
          aria-label="Loading archived hills"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              aria-hidden="true"
              className="h-40 animate-pulse rounded-lg border border-slate-200 bg-slate-50"
            />
          ))}
        </output>
      ) : archivedQuery.isError ? (
        <div
          role="alert"
          className="flex flex-col gap-4 rounded-lg border border-red-200 bg-red-50 p-6 text-red-700 sm:flex-row sm:items-start sm:justify-between"
        >
          <div>
            <p className="font-medium">Couldn't load archived hills.</p>
            <p className="mt-1 text-sm">{archivedQuery.error.message}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => archivedQuery.refetch()}
            disabled={archivedQuery.isFetching}
            className="shrink-0 border-red-300 text-red-700 hover:bg-red-100"
          >
            {archivedQuery.isFetching ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      ) : (
        <HillList hills={archivedQuery.data?.hills ?? []} mode="archive" />
      )}
    </section>
  );
}
